// Custom completion provider para los archivos YAML del bot. Aporta dos
// tipos de sugerencia que monaco-yaml (basado en JSON Schema) NO ofrece:
//
// 1. **Interpolaciones `{reply.body}`, `{session.x}`, `{flow.x}` …**
//    Cuando el operador escribe `{` dentro de un string YAML (típicamente
//    `body:`), sugerimos los namespaces y campos accesibles en runtime.
//    Estos NO están en el JSON Schema porque son strings interpolables,
//    no campos del schema.
//
// 2. **Snippets de keys de step y de mensaje**.
//    En un step, sugerir `send_message`, `wait_for`, `goto`, etc. con su
//    skeleton expandido. Igual para los `type: text|interactive_buttons|…`
//    de un mensaje.
//
// El provider es WhatsApp-agnostic: las mismas sugerencias funcionan para
// Cloud API y Evolution Go (ambos providers consumen el mismo DSL).

import type { IPosition, IRange, languages, editor as MonacoEditor } from 'monaco-editor';

type Monaco = typeof import('monaco-editor');

// ─── Vocabulario interpolable (corre en runtime via TemplateContext) ─────

interface InterpolationDef {
  /** Path completo: `reply.body`, `session.userName`, etc. */
  path: string;
  /** Etiqueta amigable para el dropdown. */
  detail: string;
  /** Markdown extendido con ejemplo. */
  doc: string;
}

const INTERPOLATIONS: InterpolationDef[] = [
  // reply.* — input del usuario que disparó este turno
  {
    path: 'reply.body',
    detail: 'Texto del mensaje del usuario',
    doc:
      'El texto literal que el usuario envió. Disponible cuando el flow se reanuda tras un `wait_for`.\n\n' +
      'Ejemplo: `body: "Dijiste: {reply.body}"`',
  },
  {
    path: 'reply.id',
    detail: 'ID del botón / fila tocados',
    doc:
      'El `id` del botón (`interactive_buttons`) o la fila (`interactive_list`) que el usuario tocó. ' +
      'No disponible para mensajes de texto.\n\nEjemplo: `body: "Elegiste: {reply.id}"`',
  },
  {
    path: 'reply.title',
    detail: 'Título visible del botón / fila tocados',
    doc:
      'El texto visible del botón / fila que el usuario tocó.\n\n' +
      'Ejemplo: `body: "Confirmaste: {reply.title}"`',
  },

  // session.* — datos persistentes de la conversación
  {
    path: 'session.userName',
    detail: 'Nombre del usuario (campo común)',
    doc:
      'Convención: lo seteás con `db_update target: session.userName` tras preguntarlo. ' +
      'Persiste durante toda la sesión.\n\nEjemplo: `body: "¡Hola, {session.userName}!"`',
  },
  {
    path: 'session.user_phone',
    detail: 'Teléfono del usuario',
    doc: 'WhatsApp ID del usuario en formato E.164 sin `+`. Disponible siempre.',
  },
  {
    path: 'session.<custom>',
    detail: 'Cualquier campo de sessionData',
    doc:
      'Reemplaza `<custom>` por la key que hayas guardado con `set_session` ' +
      '(o con `target: session.x` en una acción de BD). Ver `sessions` en docs.',
  },

  // flow.* — vars del flow actual (resultados de db_query, ts_action targets)
  {
    path: 'flow.lastInput',
    detail: 'Último input del usuario (alias)',
    doc:
      'Alias persistente de `reply.body`. Disponible incluso después de que el reply original ' +
      'se haya consumido.\n\nEjemplo: `body: "Tu mensaje fue: {flow.lastInput}"`',
  },
  {
    path: 'flow.<target>',
    detail: 'Resultado de db_query o ts_action',
    doc:
      'Reemplaza `<target>` por el nombre que pusiste en el campo `target:` de un step `action`. ' +
      'Para `db_query`, es un array — accedé con `{flow.<target>.length}` o `{flow.<target>[0].field}`.',
  },

  // db.* — alias de flow.* para queries
  {
    path: 'db.<target>',
    detail: 'Alias de flow.<target> para db_query',
    doc: 'Mismo valor que `flow.<target>`. Convención: usar `db.` cuando la fuente fue un `db_query`.',
  },

  // contact.* — datos del contacto que envia el mensaje
  {
    path: 'contact.phone',
    detail: 'Teléfono del usuario (E.164 sin `+`)',
    doc:
      'Numero del usuario que envio el mensaje. Equivalente a `{session.user_phone}` pero sin ' +
      'depender de que vos lo hayas seteado en sessionData.\n\n' +
      'Ejemplo: `body: "Te respondemos al {contact.phone}"`',
  },
  {
    path: 'contact.name',
    detail: 'Nombre visible de WhatsApp del contacto',
    doc:
      'PushName de WhatsApp (Evolution Go) o profile.name (Cloud API). Disponible automáticamente ' +
      'sin que el flow tenga que preguntar primero.\n\n' +
      'Ejemplo: `body: "¡Hola, {contact.name}!"`',
  },
  {
    path: 'contact.jid',
    detail: 'JID completo del contacto (Evolution Go)',
    doc:
      'JID del usuario (`<phone>@s.whatsapp.net`). Solo disponible para Evolution Go. ' +
      'Útil si necesitás registrar el JID exacto en alguna integración.',
  },
  {
    path: 'contact.lid',
    detail: 'LID alternativo del contacto (whatsmeow)',
    doc:
      'Identificador alternativo de Meta (`<id>@lid`). Solo disponible para builds whatsmeow ' +
      'recientes de Evolution Go. No emitido por Cloud API.',
  },
];

// ─── Snippets de claves del DSL ───────────────────────────────────────

interface SnippetDef {
  label: string;
  detail: string;
  insertText: string;
  doc: string;
}

const STEP_SNIPPETS: SnippetDef[] = [
  {
    label: 'send_message (text)',
    detail: 'Enviar un mensaje de texto',
    insertText: 'send_message:\n  type: text\n  body: "${1:Mensaje}"',
    doc: 'Envía un mensaje de texto y avanza al siguiente step. Soporta interpolación `{namespace.key}`.',
  },
  {
    label: 'send_message (interactive_buttons)',
    detail: 'Enviar 1–3 botones tappables',
    insertText:
      'send_message:\n  type: interactive_buttons\n  body: "${1:Pregunta}"\n  buttons:\n    - { id: "${2:yes}", title: "${3:Sí}" }\n    - { id: "${4:no}", title: "${5:No}" }',
    doc: 'Hasta 3 botones. El reply llega como `reply.id` y `reply.title` en `conditions` y `{reply.id}` en mensajes.',
  },
  {
    label: 'send_message (interactive_list)',
    detail: 'Enviar lista con secciones',
    insertText:
      'send_message:\n  type: interactive_list\n  body: "${1:Elige una opción}"\n  button: "${2:Ver opciones}"\n  sections:\n    - title: "${3:Categoría}"\n      rows:\n        - { id: "${4:opt1}", title: "${5:Opción 1}", description: "${6:Detalle}" }',
    doc: 'Hasta 10 filas totales repartidas entre 1–10 secciones. El reply llega como `reply.id`.',
  },
  {
    label: 'send_message (image)',
    detail: 'Enviar imagen',
    insertText:
      'send_message:\n  type: image\n  url: "${1:https://example.com/img.jpg}"\n  caption: "${2:Caption opcional}"',
    doc: 'PNG o JPG. URL pública (HTTPS). Caption opcional, máx 1024 chars.',
  },
  {
    label: 'wait_for',
    detail: 'Pausar hasta input del usuario',
    insertText:
      'wait_for: "${1:user_input}"\nconditions:\n  - if: "${2:reply.id == \'yes\'}"\n    goto: "${3:next_flow}"\n  - else: true\n    send_message:\n      type: text\n      body: "${4:No entendí}"',
    doc: 'El nombre `wait_for: "..."` es simbólico (logging). El reply llega en `reply.body`/`reply.id`/`reply.title`.',
  },
  {
    label: 'goto',
    detail: 'Saltar a otro flow',
    insertText: 'goto: "${1:flow_name}"',
    doc: 'Termina el flow actual y arranca el especificado desde el step 0.',
  },
  {
    label: 'use_component',
    detail: 'Invocar sub-flow reutilizable',
    insertText: 'use_component: "${1:component-name}"\nparams:\n  ${2:key}: "${3:value}"',
    doc: 'El component se ejecuta en línea; al terminar, el flow continúa al siguiente step.',
  },
  {
    label: 'action db_query',
    detail: 'Leer filas de una tabla declarada',
    insertText:
      'action:\n  type: db_query\n  table: "${1:products}"\n  where: { ${2:active}: ${3:true} }\n  target: "${4:products}"',
    doc: 'Resultado en `flow.<target>` (array). Solo `=` en where en MVP.',
  },
  {
    label: 'action db_insert',
    detail: 'Insertar fila en una tabla',
    insertText:
      'action:\n  type: db_insert\n  table: "${1:orders}"\n  values:\n    ${2:user_phone}: "{contact.phone}"\n  target: "${3:new_order}"',
    doc: 'La fila insertada (con defaults resueltos) queda en `flow.<target>`.',
  },
  {
    label: 'action set_session',
    detail: 'Guardar en la memoria de la conversación',
    insertText: 'action:\n  type: set_session\n  set:\n    ${1:lastOrder}: "{reply.body}"',
    doc: 'Escribe en sessionData (`set`, `append`, `increment`, `unset`). Las claves de `bot.session.persist` sobreviven a la expiración.',
  },
  {
    label: 'action ts_action',
    detail: 'Función de ext/ — solo `revel-bots test`, no se deploya',
    insertText:
      'action:\n  type: ts_action\n  file: "${1:filename}"\n  function: "${2:functionName}"\n  target: "${3:result}"',
    doc: 'Solo corre en local (`revel-bots test`, con el `.js` compilado en ext/). El servidor todavía no ejecuta extensiones: guardar o deployar un bot con ts_action se rechaza. Usa set_session / db_* / conditions.',
  },
];

// ─── Detección de contexto ────────────────────────────────────────────

function isInsideInterpolation(model: MonacoEditor.ITextModel, position: IPosition): boolean {
  // Mira la línea actual hasta la posición y chequea si hay un `{` sin
  // cerrar antes. Heurística simple — funciona bien para YAML donde los
  // strings con interpolación van en una sola línea típicamente.
  const lineUntil = model.getValueInRange({
    startLineNumber: position.lineNumber,
    startColumn: 1,
    endLineNumber: position.lineNumber,
    endColumn: position.column,
  });
  const lastOpen = lineUntil.lastIndexOf('{');
  if (lastOpen === -1) return false;
  // Si hay un `}` después del último `{` y antes del cursor, ya cerrado.
  const afterOpen = lineUntil.slice(lastOpen);
  return !afterOpen.includes('}');
}

/** Heurística: cursor está al inicio de un step (después de `- id: <x>`). */
function isAtStepRoot(model: MonacoEditor.ITextModel, position: IPosition): boolean {
  const line = model.getLineContent(position.lineNumber).trim();
  // El usuario está en una línea vacía (o casi) dentro de un step.
  if (line === '' || /^[a-z_]+$/.test(line)) {
    // Mirar las líneas previas para ver si el último indent fue un `- id:`.
    for (let n = position.lineNumber - 1; n > 0 && n > position.lineNumber - 10; n--) {
      const l = model.getLineContent(n);
      if (/^\s*- id:/.test(l)) return true;
      if (/^[a-z_]+:/.test(l.trim())) return false;
    }
  }
  return false;
}

// ─── Provider ─────────────────────────────────────────────────────────

export function registerCompletionProvider(monaco: Monaco): { dispose: () => void } {
  return monaco.languages.registerCompletionItemProvider(['yaml', 'plaintext'], {
    triggerCharacters: ['{', '.', ' ', '-', '\n'],

    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position);
      const range: IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      // ── Caso 1: dentro de `{...}` → variables interpolables ─────────
      if (isInsideInterpolation(model, position)) {
        const suggestions: languages.CompletionItem[] = INTERPOLATIONS.map((i) => ({
          label: i.path,
          kind: monaco.languages.CompletionItemKind.Variable,
          detail: i.detail,
          documentation: { value: i.doc },
          insertText: i.path,
          range,
        }));
        return { suggestions };
      }

      // ── Caso 2: en step root → snippets de step keys ────────────────
      if (isAtStepRoot(model, position)) {
        const suggestions: languages.CompletionItem[] = STEP_SNIPPETS.map((s) => ({
          label: s.label,
          kind: monaco.languages.CompletionItemKind.Snippet,
          detail: s.detail,
          documentation: { value: s.doc },
          insertText: s.insertText,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
        }));
        return { suggestions };
      }

      return { suggestions: [] };
    },
  });
}
